import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { I18n } from 'helpers/i18n';
import {
  KAMBRIA_MEETING_APP_DOWNLOAD_URL_MAC_ARM,
  KAMBRIA_MEETING_APP_DOWNLOAD_URL_MAC_INTEL,
  KAMBRIA_MEETING_APP_DOWNLOAD_URL_WIN,
} from './workplaceMeetingConfig';

function IconWindows() {
  return (
    <svg
      className="kambria-app-dl-modal__os-icon kambria-app-dl-modal__os-icon--windows"
      width={40}
      height={40}
      viewBox="0 0 24 24"
      aria-hidden
    >
      <path
        fill="currentColor"
        d="M3 5.548l7.548-1.033v7.255H3V5.548zm0 8.644l7.548.988v7.255L3 20.452v-6.26zm8.452-9.7L21 3v9.77h-9.548V4.492zm0 10.708L21 14.23V24l-9.548-1.33v-7.47z"
      />
    </svg>
  );
}

function IconApple() {
  return (
    <svg
      className="kambria-app-dl-modal__os-icon kambria-app-dl-modal__os-icon--apple"
      width={36}
      height={40}
      viewBox="0 0 24 24"
      aria-hidden
    >
      <path
        fill="currentColor"
        d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"
      />
    </svg>
  );
}

function IconDownload() {
  return (
    <svg
      className="kambria-app-dl-modal__dl-icon"
      width={18}
      height={18}
      viewBox="0 0 24 24"
      aria-hidden
    >
      <path fill="currentColor" d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
    </svg>
  );
}

/**
 * Popup tải Kambria Meeting — render qua portal, không dùng react-bootstrap Modal
 * (tránh lệch class .in / .show với Bootstrap 4 trong bundle).
 */
function openInstallerUrl(url) {
  const u = url && String(url).trim();
  if (!u) return;
  window.open(u, '_blank', 'noopener,noreferrer');
}

export default function KambriaMeetingDownloadModal({ show, onHide }) {
  const winUrl = KAMBRIA_MEETING_APP_DOWNLOAD_URL_WIN;
  const macArmUrl = KAMBRIA_MEETING_APP_DOWNLOAD_URL_MAC_ARM;
  const macIntelUrl = KAMBRIA_MEETING_APP_DOWNLOAD_URL_MAC_INTEL;
  const winReady = Boolean(winUrl && String(winUrl).trim());
  const macArmReady = Boolean(macArmUrl && String(macArmUrl).trim());
  const macIntelReady = Boolean(macIntelUrl && String(macIntelUrl).trim());

  useEffect(() => {
    if (!show || typeof document === 'undefined') return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [show]);

  useEffect(() => {
    if (!show) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onHide();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [show, onHide]);

  if (!show || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div className="kambria-app-dl-overlay" role="presentation">
      <div
        className="kambria-app-dl-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="kambria-app-dl-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="kambria-app-dl-modal__body">
          <button
            type="button"
            className="kambria-app-dl-modal__close"
            onClick={onHide}
            aria-label={I18n.t('close')}
          >
            ×
          </button>

          <header className="kambria-app-dl-modal__header">
            <h2 id="kambria-app-dl-title" className="kambria-app-dl-modal__title">
              {I18n.t('meeting_join_app_dl_title')}
            </h2>
            <p className="kambria-app-dl-modal__subtitle">{I18n.t('meeting_join_app_dl_subtitle')}</p>
          </header>

          <div className="kambria-app-dl-modal__grid">
            <article className="kambria-app-dl-modal__card kambria-app-dl-modal__card--windows">
              <div className="kambria-app-dl-modal__card-inner">
                <IconWindows />
                <h3 className="kambria-app-dl-modal__card-title">{I18n.t('meeting_join_app_dl_win_heading')}</h3>
                <p className="kambria-app-dl-modal__card-line kambria-app-dl-modal__card-line--en">
                  {I18n.t('meeting_join_app_dl_win_product')}
                </p>
                <p className="kambria-app-dl-modal__card-line kambria-app-dl-modal__card-line--vi">
                  {I18n.t('meeting_join_app_dl_win_support')}
                </p>
                <button
                  type="button"
                  className="kambria-app-dl-modal__btn kambria-app-dl-modal__btn--windows"
                  disabled={!winReady}
                  onClick={() => openInstallerUrl(winUrl)}
                >
                  <IconDownload />
                  <span>{I18n.t('meeting_join_app_dl_win_button')}</span>
                </button>
                <p className="kambria-app-dl-modal__meta">{I18n.t('meeting_join_app_dl_win_size')}</p>
              </div>
            </article>

            <article className="kambria-app-dl-modal__card kambria-app-dl-modal__card--mac">
              <div className="kambria-app-dl-modal__card-inner">
                <IconApple />
                <h3 className="kambria-app-dl-modal__card-title">{I18n.t('meeting_join_app_dl_mac_heading')}</h3>
                <p className="kambria-app-dl-modal__card-line kambria-app-dl-modal__card-line--en">
                  {I18n.t('meeting_join_app_dl_mac_product')}
                </p>
                <p className="kambria-app-dl-modal__card-line kambria-app-dl-modal__card-line--vi">
                  {I18n.t('meeting_join_app_dl_mac_support')}
                </p>

                <div className="kambria-app-dl-modal__mac-builds">
                  <button
                    type="button"
                    className="kambria-app-dl-modal__btn kambria-app-dl-modal__btn--mac"
                    disabled={!macArmReady}
                    onClick={() => openInstallerUrl(macArmUrl)}
                  >
                    <IconDownload />
                    <span>{I18n.t('meeting_join_app_dl_mac_arm_button')}</span>
                  </button>
                  <button
                    type="button"
                    className="kambria-app-dl-modal__btn kambria-app-dl-modal__btn--mac"
                    disabled={!macIntelReady}
                    onClick={() => openInstallerUrl(macIntelUrl)}
                  >
                    <IconDownload />
                    <span>{I18n.t('meeting_join_app_dl_mac_intel_button')}</span>
                  </button>
                </div>

                <p className="kambria-app-dl-modal__meta">{I18n.t('meeting_join_app_dl_mac_size')}</p>
                <p className="kambria-app-dl-modal__mac-hint">{I18n.t('meeting_join_app_dl_mac_hint')}</p>
              </div>
            </article>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
